import { useEffect, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import type { Account } from "@/lib/finance";
import { moneyIn, todayISO } from "@/lib/format";
import { MoneyInput, parseMoney } from "@/components/MoneyInput";
import { DateInput } from "@/components/DateInput";

interface Props { open: boolean; onOpenChange: (o: boolean) => void; accounts: Account[]; initialId: string | null; onSaved: () => void }

const REASONS = [
  { value: "unrecorded", label: "Gastos que no registré" }, { value: "bank_fee", label: "Comisión del banco" },
  { value: "interest", label: "Intereses ganados" }, { value: "other", label: "Otro" },
];

export function AdjustmentDialog({ open, onOpenChange, accounts, initialId, onSaved }: Props) {
  const [accountId, setAccountId] = useState("");
  const [real, setReal] = useState("");
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const acc = accounts.find((a) => a.id === accountId);
  const investment = acc?.account_type === "investment";
  const realNum = parseMoney(real);
  const target = acc && !Number.isNaN(realNum) && real.trim() ? (acc.is_liability ? -Math.abs(realNum) : realNum) : null;
  const diff = acc && target !== null ? Math.round((target - acc.current_balance) * 100) / 100 : null;

  useEffect(() => {
    if (open) { setAccountId(initialId ?? accounts[0]?.id ?? ""); setReal(""); setNotes(""); setReason(""); setDate(todayISO()); }
  }, [open, initialId, accounts]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!acc) return toast.error("Elige una cuenta");
    if (Number.isNaN(parseMoney(real))) return toast.error(investment ? "Indica el valor actual" : "Indica el saldo real");
    if (!date) return toast.error("Escribe la fecha como dd/mm/aaaa");
    if (!investment && !reason) return toast.error("Elige el motivo del ajuste");
    if (!investment && reason === "other" && !notes.trim()) return toast.error("Escribe el motivo del ajuste");
    setSaving(true);
    try {
      await api.post(`/accounts/${acc.id}/adjustment`, { real_balance: parseMoney(real), date, notes: notes.trim(), reason: investment ? undefined : reason });
      toast.success(investment ? "Valor actualizado" : "Ajuste registrado");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-md" data-testid="adjustment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1B2A4A]">{investment ? "Actualizar valor" : "Registrar ajuste"}</DialogTitle>
          <DialogDescription>
            {investment
              ? "Escribe cuánto vale hoy tu inversión. La diferencia se registra como Rendimiento (positivo o negativo) y no cuenta como ingreso del mes."
              : "Escribe el saldo real. Creamos un movimiento de ajuste por la diferencia; tu saldo inicial no cambia."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="adjustment-form">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Cuenta</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="h-11" data-testid="adjustment-account-select"><SelectValue placeholder="Elige una cuenta" /></SelectTrigger>
              <SelectContent className="bg-white">
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {acc && <p className="text-xs text-slate-500" data-testid="adjustment-current-balance">Saldo calculado hoy: <b className="tabular">{moneyIn(acc.current_balance, acc.currency)}</b></p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adj-real" className="text-sm text-slate-700">{investment ? "Valor actual hoy" : acc?.is_liability ? "Deuda real hoy (en positivo)" : "Saldo real hoy"}</Label>
            <MoneyInput id="adj-real" value={real} onChange={setReal} className="h-11" testId="adjustment-real-input" />
            {diff !== null && (
              <p className={`text-sm font-semibold ${diff < 0 ? "text-[#B23A1E]" : diff > 0 ? "text-[#1B6E4F]" : "text-slate-500"}`} data-testid="adjustment-diff">
                {diff === 0 ? "El saldo ya coincide" : `Vas a ajustar ${diff < 0 ? "−" : "+"}${moneyIn(Math.abs(diff), acc?.currency)}`}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adj-date" className="text-sm text-slate-700">Fecha</Label>
            <DateInput id="adj-date" value={date} onChange={setDate} testId="adjustment-date-input" />
          </div>
          {!investment && (
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Motivo</Label>
              <div className="flex flex-wrap gap-2" data-testid="adjustment-reasons">
                {REASONS.map((r) => (
                  <button key={r.value} type="button" onClick={() => setReason(r.value)} data-testid={`adjustment-reason-${r.value}`}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${reason === r.value ? "border-[#1B2A4A] bg-[#1B2A4A] text-white" : "border-[#DDE1E8] text-[#1B2A4A] hover:border-[#C9A84C]"}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {(investment || reason === "other") && (
          <div className="space-y-1.5">
            <Label htmlFor="adj-notes" className="text-sm text-slate-700">{investment ? "Nota (opcional)" : "Escribe el motivo (obligatorio)"}</Label>
            <Textarea id="adj-notes" rows={2} value={notes} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)} data-testid="adjustment-notes-input" />
          </div>
          )}
          <Button type="submit" disabled={saving} className="h-11 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="adjustment-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : investment ? "Actualizar valor" : "Registrar ajuste"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
